import { BaseModuleProvider } from '@equinor/fusion-framework-module/provider';
import { version } from './version';
import { ClientNotFoundException } from './errors/index.js';
/** URL protocols accepted as valid ad-hoc base URIs. */
const SUPPORTED_PROTOCOLS = ['http:', 'https:', 'ws:', 'wss:'];
/**
 * Checks if a given string is a valid absolute URL with a supported protocol.
 * @param url - The string to check.
 * @returns `true` when the string uses one of {@link SUPPORTED_PROTOCOLS}.
 */
const isURL = (url) => {
    try {
        const parsed = new URL(url);
        return SUPPORTED_PROTOCOLS.includes(parsed.protocol);
    }
    catch {
        return false;
    }
};
/**
 * Heuristic check for strings that look like a bare hostname or URL without a
 * protocol prefix (e.g. `api.example.com` or `api.example.com/v1`).
 *
 * Used to emit a deprecation warning when callers rely on the old (pre-patch)
 * behaviour that accepted protocol-less URLs.
 */
const looksLikeURL = (value) => !value.includes(' ') && /^[a-z\d]([a-z\d-]*\.)+[a-z]{2,}/i.test(value);
/**
 * The `HttpClientProvider` class is responsible for managing HTTP client instances and their configuration.
 * It provides methods to check if a client is configured, create new client instances, and create custom client instances.
 */
export class HttpClientProvider extends BaseModuleProvider {
    config;
    /**
     * Gets the default HTTP request handler for the HTTP client provider.
     * @returns The default HTTP request handler.
     */
    get defaultHttpRequestHandler() {
        return this.config.defaultHttpRequestHandler;
    }
    /**
     * Gets the default middleware chain for the HTTP client provider.
     * @returns The default middleware chain.
     */
    get defaultHttpMiddlewareHandler() {
        return this.config.defaultHttpMiddlewareHandler;
    }
    /**
     * Creates a new `HttpClientProvider`.
     * @param config - The configurator providing client definitions and defaults.
     */
    constructor(config) {
        super({
            version,
            config,
        });
        this.config = config;
    }
    /**
     * Checks if a client with the given key is configured in the `HttpClientProvider`.
     * @param key - The key of the HTTP client to check.
     * @returns `true` if a client with the given key is configured, `false` otherwise.
     */
    hasClient(key) {
        return Object.keys(this.config.clients).includes(key);
    }
    /**
     * Creates a fresh HTTP client instance from a named or ad-hoc configuration.
     *
     * @param keyOrConfig - The key or configuration object for the HTTP client.
     * @returns The created HTTP client instance.
     *
     * @remarks
     * This method resolves the configuration for the HTTP client based on the provided `keyOrConfig` parameter.
     * If a string is provided, it is treated as the key for a pre-configured client in the `HttpClientProvider`.
     * If an `HttpClientOptions` object is provided, it is used as the configuration for the new client instance.
     *
     * The method applies `baseUri`, `defaultScopes`, `requestHandler`, `responseHandler`,
     * a custom `ctor` when configured, and finally runs `onCreate` for the newly created instance.
     *
     * The created HTTP client instance is returned.
     */
    createClient(keyOrConfig) {
        const config = this._resolveConfig(keyOrConfig);
        const { baseUri, defaultScopes = [], onCreate, ctor = this.config.defaultHttpClientCtor, requestHandler = this.defaultHttpRequestHandler, responseHandler, middlewareHandler = this.defaultHttpMiddlewareHandler, } = config;
        const options = { requestHandler, responseHandler, middlewareHandler };
        const instance = new ctor(baseUri || '', options);
        // attach the resolved default scopes onto the instance without overwriting other own properties
        Object.assign(instance, { defaultScopes });
        onCreate?.(instance);
        return instance;
    }
    /**
     * Creates a client instance and returns it as the requested custom client type.
     *
     * @template T - The custom `HttpClient` implementation type to cast the created instance to.
     * @param key - The key of the pre-configured HTTP client to create.
     * @returns The created HTTP client instance, cast to the specified type `T`.
     *
     * @remarks
     * This method delegates to the `createClient` method, but casts the returned
     * instance to the specified type `T`. This can be useful when you need to
     * work with a specific HTTP client implementation, but the `HttpClientProvider`
     * is configured to use a different implementation.
     */
    createCustomClient(key) {
        // `createClient` always returns the provider's configured `HttpClient` implementation —
        // cast through `unknown` to hand back the caller-requested implementation type `T`.
        return this.createClient(key);
    }
    /**
     * Resolves the configuration for an HTTP client based on the provided `keyOrConfig` parameter.
     *
     * If a string is provided, it is treated as either a pre-configured client key or,
     * when it is an absolute `http:` or `https:` URL, as an ad-hoc `baseUri`.
     * If an `HttpClientOptions` object is provided, it is used directly as the configuration for the new client instance.
     *
     * @param keyOrConfig - The key or configuration object for the HTTP client.
     * @returns The resolved HTTP client configuration.
     * @throws {ClientNotFoundException} When `keyOrConfig` is a string that is neither a registered
     * client key nor a URL-like value.
     */
    _resolveConfig(keyOrConfig) {
        // a string may reference a registered client key or an ad-hoc URL; anything else is used as-is
        if (typeof keyOrConfig === 'string') {
            const config = this.config.clients[keyOrConfig];
            // an absolute http(s) URL can be used directly as an ad-hoc baseUri
            if (!config && isURL(keyOrConfig)) {
                return { baseUri: keyOrConfig };
            }
            else if (!config && looksLikeURL(keyOrConfig)) {
                // recover from a missing protocol instead of failing outright, but warn so it can be fixed
                console.warn(`[HttpClientProvider] "${keyOrConfig}" looks like a URL but is missing the http:// or https:// protocol. ` +
                    `Treating it as "https://${keyOrConfig}". ` +
                    `Pass a fully-qualified URL to silence this warning.`);
                return { baseUri: `https://${keyOrConfig}` };
            }
            else if (!config) {
                throw new ClientNotFoundException(`No registered http client for key [${keyOrConfig}]`);
            }
            return config;
        }
        return keyOrConfig;
    }
}
//# sourceMappingURL=provider.js.map